//#region IMPORTS
import {
  HANDLE_PRESET_KEY,
  APPROVE_PRESET_KEY,
  SEND_PRESET_KEY,
  STANDALONE_PRESETS,
} from "./promoPresets";
import { useAppSelector } from "../../../store/configureStore";
import { Fragment, ReactNode } from "react";
import {
  HandThumbUpIcon,
  Cog6ToothIcon,
  TruckIcon,
  ChevronRightIcon,
} from "@heroicons/react/20/solid";
import classNames from "../../../helpers/classNames";
//#endregion

//#region INTERFACE
interface Props {
  activeKey: string;
  onSelect: (key: string) => void;
  isPicker: boolean;
  isAdmin: boolean;
  isHq: boolean;
}
//#endregion

export default function PromoPresetPills({
  activeKey,
  onSelect,
  isPicker,
  isAdmin,
  isHq,
}: Props) {
  //#region SETUP
  const { orders_ordered, orders_pending, orders_inProgress } = useAppSelector(
    (state) => state.orders,
  );

  const pillButton = (
    key: string,
    label: string,
    count: number,
    showCount: boolean,
    disabled: boolean = false,
    icon?: ReactNode,
    highlight: boolean = false,
    badgeColor: string = "bg-purple-600 text-white",
  ) => {
    const active = activeKey === key;
    return (
      <button
        key={key}
        onClick={() => onSelect(key)}
        disabled={disabled}
        className={classNames(
          "relative inline-flex items-center gap-1 px-3 py-1 text-sm font-medium rounded-md border transition",
          disabled
            ? "bg-gray-50 text-gray-400 border-gray-200 cursor-not-allowed"
            : active
              ? "bg-gray-300 text-gray-900 border-gray-300"
              : "bg-white text-gray-700 border-gray-300 hover:bg-gray-50",
        )}
      >
        {icon}
        {label}
        {showCount && count > 0 && (
          <span
            className={classNames(
              "absolute -top-2 -right-2 min-w-[1.25rem] h-5 px-1 inline-flex items-center justify-center rounded-full text-xs font-semibold ring-2 ring-white",
              badgeColor,
            )}
          >
            {count}
          </span>
        )}
      </button>
    );
  };

  // Fulfilment workflow group, shown with arrows: Approve → Manage → Send.
  const workflowPills = [
    // Promo approve is HQ / admin only — pickers do not approve promo orders.
    (isHq || isAdmin) &&
      pillButton(
        APPROVE_PRESET_KEY,
        "Approve",
        orders_pending,
        true,
        orders_pending === 0,
        <HandThumbUpIcon className="w-4 h-4" />,
        // Ping for the role whose workflow it is (HQ); admin = calm.
        isHq,
        "bg-emerald-500 text-white",
      ),
    (isPicker || isAdmin) &&
      pillButton(
        HANDLE_PRESET_KEY,
        "Manage",
        orders_ordered,
        true,
        orders_ordered === 0,
        <Cog6ToothIcon className="w-4 h-4" />,
        isPicker,
        "bg-amber-600 text-white",
      ),
    (isPicker || isAdmin) &&
      pillButton(
        SEND_PRESET_KEY,
        "Send",
        orders_inProgress,
        true,
        orders_inProgress === 0,
        <TruckIcon className="w-4 h-4" />,
        isPicker,
        "bg-sky-700 text-white",
      ),
  ].filter(Boolean);

  // Read-only overviews on the right (Original / All).
  const overviewPresets = STANDALONE_PRESETS.filter((p) => !p.privilegedOnly);
  //#endregion

  //#region RENDER
  return (
    // Flow group sits on the right next to the overview pills. The gap between
    // the group and the overview matches the gap-2 between the flow pills so the
    // spacing reads evenly (the flow-pill gap is leading; it also holds the
    // chevrons shown for some roles).
    <div className="flex flex-1 items-center justify-end gap-2">
      {/* Workflow group: Approve → Manage → Send */}
      {workflowPills.length > 0 && (
        <div className="flex items-center gap-2">
          {workflowPills.map((pill, i) => (
            <Fragment key={i}>
              {i > 0 && (
                <ChevronRightIcon
                  className="w-4 h-4 text-gray-400 shrink-0"
                  aria-hidden="true"
                />
              )}
              {pill}
            </Fragment>
          ))}
        </div>
      )}

      {/* Overview presets */}
      <div className="flex items-center gap-2">
        {overviewPresets.map(({ key, label }) => {
          const active = activeKey === key;
          return (
            <button
              key={key}
              onClick={() => onSelect(key)}
              className={classNames(
                "px-3 py-1 text-sm font-medium rounded-md border transition",
                active
                  ? "bg-gray-300 text-gray-900 border-gray-300"
                  : "bg-white text-gray-700 border-gray-300 hover:bg-gray-50",
              )}
            >
              {label}
            </button>
          );
        })}
      </div>
    </div>
  );
  //#endregion
}
